import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import format from '@st-operations/uikit/lib/format.js'
import { Card, CARD_THEME, IconButton, Modal, Radio } from '@st-operations/uikit/src/v2/index'

import { getEventsByDate, getUsersByDate } from '_services/data-explorer'

import { useRequestConfig, useModal } from '_hooks'
import { useEventFeedsContext } from '../../../context/index.jsx'
import AverageCountsPerInterval from '../counts-card/average-counts-per-interval'
import { logErrorAsync } from '../../../../../lib/utility.js'

import { useIsSnapshot } from '../../../manage-component/index.jsx'
import { TIME_INTERVAL_VALUES } from '_components/interval-select/index.jsx'
import AverageCounts from './average-counts/index.jsx'
import PeriodSelectStored from '_components/period-select-stored/index.jsx'
import IntervalSelectStored from '_components/interval-select-stored/index.jsx'
import SettingsToggle from '_components/settings-toggle/index.jsx'
import ExpandButton from '_components/expand-button/index.jsx'
import { TYPE_PREBUILT_ATTRIBUTES_V2 } from '../../../wizard/index.jsx'
import { getPredictorsUserCounts } from '_services/data-explorer.js'
import LineUserCountsChart from '_components/line-user-counts-chart/index.jsx'
import camelCaseToSnakeCase from '_lib/camelCaseToSnakeCase.js'

import './styles.scss'

const MODAL_STYLES = {
  content: 'expanded-counts__container',
  childWrapper: 'expanded-counts__wrapper',
  innerWrapper: 'expanded-counts__inner-wrapper',
}

const getPastYear = () => {
  const date = new Date()

  const [year, month] = [date.getFullYear() - 1, date.getMonth() + 2]

  return `${year}-${month}`
}

function CountsCard({ className }) {
  const {
    data: feedData,
    // permissions: {
    //   tabs: { scale_and_coverage },
    // },
  } = useEventFeedsContext()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({})
  const [showUserChart, setShowUserChart] = useState(false)
  const { start, end, interval } = useRequestConfig(
    feedData.type === TYPE_PREBUILT_ATTRIBUTES_V2
      ? TYPE_PREBUILT_ATTRIBUTES_V2
      : showUserChart
      ? 'usersPerDay'
      : 'eventsPerDay'
  )
  const predictors = feedData.type === TYPE_PREBUILT_ATTRIBUTES_V2
  const isSnapshot = useIsSnapshot()
  const settings = useModal(false)
  const expanded = useModal(false)

  const [hoveredKeyword, setHoveredKeyword] = useState()

  useEffect(() => {
    setShowUserChart(feedData.type === TYPE_PREBUILT_ATTRIBUTES_V2)
  }, [feedData])

  useEffect(() => {
    if (feedData._id) {
      const storedData = localStorage.getItem(
        `st-event-feeds__counts-card__${feedData._id}/userChart`
      )

      if (storedData === 'true') {
        setShowUserChart(true)
      }
    }
  }, [feedData._id])

  useEffect(() => {
    async function getDataCount() {
      const getDataFunction = predictors
        ? getPredictorsUserCounts
        : showUserChart
        ? getUsersByDate
        : getEventsByDate

      setLoading(true)
      try {
        const response = isSnapshot
          ? feedData.snapshot.eventsByDate
          : await getDataFunction({
              filters: [...feedData.filters],
              attributes: feedData.attributes,
              start,
              end,
              interval,
            })
        setData(response)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
      setLoading(false)
    }

    if (start && end && interval) {
      getDataCount()
    }
  }, [
    predictors,
    feedData.filters,
    feedData.snapshot,
    feedData.attributes,
    start,
    end,
    interval,
    showUserChart,
    feedData.visualizations?.predictors?.prebuiltAttribute,
  ])

  const handleToggleUserChart = (event) => {
    event.persist()
    setShowUserChart(event?.target?.value === 'user')
    localStorage.setItem(
      `st-event-feeds__counts-card__${feedData._id}/userChart`,
      event?.target?.value === 'user'
    )
  }

  const getAverageCounts = () => {
    const userData = {
      [TIME_INTERVAL_VALUES.DAILY]: data?.usersPerDay,
      [TIME_INTERVAL_VALUES.MONTHLY]: data?.usersPerMonth,
    }

    const eventsData = {
      [TIME_INTERVAL_VALUES.DAILY]: data?.eventsPerDay,
      [TIME_INTERVAL_VALUES.WEEKLY]: data?.eventsPerWeek,
      [TIME_INTERVAL_VALUES.MONTHLY]: data?.eventsPerMonth,
    }

    return showUserChart ? userData[interval] : eventsData[interval]
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        settings.handleCloseModal()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [settings])

  const SettingsButton = () => (
    <SettingsToggle
      label={`From ${format('date', start)} to ${format('date', end)}, ${
        interval === TIME_INTERVAL_VALUES.DAILY
          ? 'Daily'
          : interval === TIME_INTERVAL_VALUES.WEEKLY
          ? 'Weekly'
          : interval === TIME_INTERVAL_VALUES.MONTHLY
          ? 'Monthly'
          : ''
      }`}
    >
      {({ onClose }) => (
        <Card className="counts-config__dialog">
          <IconButton
            onClick={onClose}
            aria-label="Close modal"
            className={classnames('st-modal__header--close')}
            iconProps={{
              icon: 'delete',
              type: 'fill',
              width: 16,
              height: 16,
              color: '#000',
            }}
          />

          <div>
            <p className="counts-config__dialog-text">Show statistics for:</p>
            <div className="counts-config__radio-group">
              <Radio
                name="chart_type"
                value="events"
                onChange={handleToggleUserChart}
                label="Events"
                checked={!showUserChart}
              />
              {/* DISABLE USERS DISPLAY OPTION */}
              {/* {(scale_and_coverage?.items.includes('users') ||
                scale_and_coverage?.items.includes('all')) && (
                <Radio
                  name="chart_type"
                  value="user"
                  onChange={handleToggleUserChart}
                  label="Users"
                  checked={showUserChart}
                />
              )} */}
            </div>
          </div>
          <PeriodSelectStored
            visualizationsPath={showUserChart ? 'usersPerDay' : 'eventsPerDay'}
            minDate={getPastYear()}
            vertical
          />
          <IntervalSelectStored
            visualizationsPath={showUserChart ? 'usersPerDay' : 'eventsPerDay'}
            type={showUserChart ? 'users' : 'events'}
          />
        </Card>
      )}
    </SettingsToggle>
  )

  return (
    <Card
      component="section"
      theme={CARD_THEME.NO_SHADOW}
      className={classnames('counts-card', className)}
    >
      <header className="counts-card__header">
        <div className="counts-card__title-container">
          <If condition={!predictors}>
            <h1 className="counts-card__title">Events</h1>
          </If>
          <If condition={predictors}>
            <h1 className="counts-card__title">Users</h1>
          </If>
        </div>

        <If condition={!predictors}>
          <p className="counts-card__subtitle">• Projected from a 0.1% sample</p>
        </If>
      </header>

      <div className="counts-card__info-section">
        <AverageCounts
          interval={interval}
          count={predictors ? 0 : getAverageCounts()}
          loading={loading}
          type={showUserChart ? 'users' : 'events'}
        />
        <div className="counts-config">
          <div className="counts-config__buttons">
            <div className="counts-config__dialog-container">
              <If condition={feedData.type !== TYPE_PREBUILT_ATTRIBUTES_V2}>
                <SettingsButton />
              </If>
              <ExpandButton onClick={expanded.handleOpenModal} />
            </div>
          </div>
        </div>
      </div>

      <div className="counts-card__chart">
        <If condition={!predictors}>
          <AverageCountsPerInterval
            data={showUserChart ? data?.usersByDate : data?.eventsByDate}
            interval={interval}
            type={showUserChart ? 'users' : 'events'}
          />
        </If>
        <If condition={predictors}>
          <LineUserCountsChart
            data={data.attributes}
            interval={TIME_INTERVAL_VALUES.DAILY}
            hoveredKeyword={hoveredKeyword}
            onHoveredKeywordChange={(value) => setHoveredKeyword(value)}
            transformKeyword={camelCaseToSnakeCase}
          />
        </If>
      </div>

      <Modal isOpen={expanded.isOpen} classes={MODAL_STYLES} noCloseButton onClose={() => {}}>
        <div className="expanded-counts">
          <div className="expanded-counts__header-container">
            <header className="counts-card__header">
              <div className="counts-card__title-container">
                <h1 className="counts-card__title">Events</h1>
              </div>

              <If condition={!predictors}>
                <p className="counts-card__subtitle">• Projected from a 0.1% sample</p>
              </If>
            </header>
            <div className="expanded-counts__meta">
              <AverageCounts
                interval={interval}
                count={getAverageCounts()}
                loading={loading}
                type={showUserChart ? 'users' : 'events'}
              />
              <div className="counts-config__dialog-container">
                <If condition={feedData.type !== TYPE_PREBUILT_ATTRIBUTES_V2}>
                  <SettingsButton />
                </If>
                <ExpandButton isExpanded={true} onClick={expanded.handleCloseModal} />
              </div>
            </div>
          </div>
          <div className="counts-card__chart">
            <If condition={!predictors}>
              <AverageCountsPerInterval
                data={
                  predictors
                    ? data.userCounts
                    : showUserChart
                    ? data?.usersByDate
                    : data?.eventsByDate
                }
                interval={interval}
                type={showUserChart ? 'users' : 'events'}
              />
            </If>
            <If condition={predictors}>
              <LineUserCountsChart
                data={data.attributes}
                interval={TIME_INTERVAL_VALUES.DAILY}
                hoveredKeyword={hoveredKeyword}
                onHoveredKeywordChange={(value) => setHoveredKeyword(value)}
                transformKeyword={camelCaseToSnakeCase}
              />
            </If>
          </div>
        </div>
      </Modal>
    </Card>
  )
}

export default CountsCard
